/**
 * A message's text with its pictures where the text places them.
 *
 * `[picture N]` on a line of its own draws picture N there; a picture no line
 * names follows the text, which is how every message written before this
 * reads — and how a reporter's message, whose pictures are simply attached,
 * still reads. One component for the staff thread, the reporter's panel and a
 * draft, so a reply looks the same before and after it is sent.
 */
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import type { SupportAttachment } from "../../api/support-tickets";
import SupportAttachmentImage from "./support-attachment-image";
import { pictureToken, splitPictureTokens } from "./support-picture-tokens";

interface SupportMessageBodyProps {
	body: string;
	pictures: SupportAttachment[];
	/** Staff only: what the browser recorded with the reporter's capture. */
	context?: string;
}

const SupportMessageBody: React.FC<SupportMessageBodyProps> = ({
	body,
	pictures,
	context,
}) => {
	const parts = splitPictureTokens(body);
	const placed = new Set(
		parts.flatMap((part) =>
			part.kind === "picture" && pictures[part.number - 1] ? [part.number] : []
		)
	);
	const picture = (attachment: SupportAttachment) => (
		<SupportAttachmentImage
			key={attachment.id}
			attachment={attachment}
			context={context}
		/>
	);
	return (
		<Box data-component="support-message-body">
			{parts.map((part, index) => {
				if (part.kind === "text") {
					return (
						<Typography
							key={`text-${index}`}
							variant="body2"
							sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}
						>
							{part.text}
						</Typography>
					);
				}
				const attachment = pictures[part.number - 1];
				// A line naming a picture that is not there stays as written.
				return attachment ? (
					picture(attachment)
				) : (
					<Typography
						key={`missing-${index}`}
						variant="body2"
						sx={{ mt: 0.5 }}
					>
						{pictureToken(part.number)}
					</Typography>
				);
			})}
			{pictures.filter((_, index) => !placed.has(index + 1)).map(picture)}
		</Box>
	);
};

export default SupportMessageBody;
